import {TLQQuoteTermLength} from '@amicamutual/amica-common';

export enum EnsightenActionResult {
  SUCCESSFUL = 'Successful',
  UNSUCCESSFUL = 'Unsuccessful'
}

/**
 * @prop pageCategory - page category
 * @prop pageName - page name
 * @prop resolution - current device resolution
 * @prop device - current device
 * @prop timeStamp - current timestamp
 */
export interface Global {
  pageCategory: string;
  pageName: string;
  resolution: string;
  device: string;
  timeStamp: number;
  mitigationType?: string;
}

/**
 * @prop messageID - id of message
 * @prop messageType - type of message / ie error, warning, or alert
 * @prop messageDescription - description of message
 */
export interface Message {
  messageID: string;
  messageType: string;
  messageDescription: string;
}

export interface EnsightenLogItem {
  logInfo: string;
}

/**
 * @prop quoteID - used to join web quote data to other systems.
 * @prop quoteLOB - used to segment quoting data by Auto, Home, Renters, Condo, Express Renters.
 * @prop prefilledData - used to segment quoting data prefill vs non prefill.
 * @prop state
 * @prop username
 * @prop email
 * @prop partyUID - used to tie post login user activity to their policies or account information.
 * @prop activePoliciesTypes - used to segment users that login or
 *   use features, user logged in and has an auto and life policy.
 * @prop claimFileNumber - claims file number used to tie claims information to ECF.
 * @prop claimsLOB - used to segment claims by LOB, amount of claims by Auto, Home, glass, tow.
 * @prop claimStatus - used to see what the status is of the claim ie open, closed...
 * @prop policyNumber - claim policy number used to tie claims information to ECF.
 * @prop emailStatus - the notification enrollment status of the claimant's email
 * @prop policies - list with abbreviated info about user's policies
 * @prop claims - list with abbreviated info about user's recent claims
 * @prop phototypesUploaded - mime-types of the photo types uploaded via the home photo tool
 * @prop photosUploaded - number of photos uploaded via the home photo tool
 * @prop photosSize - size in bytes, of all the photos uploaded via the home photo tool
 */

export interface EASEnsightenOfferings {
  [lob: string]: {
    quoteId?: string;
    isInitial?: boolean;
    offerings?: {
      [offering: string]: ReadonlyArray<any> | undefined;
    };
  }[];
}
export interface Quote {
  quoteLOB: string;
  state: any;
  readonly quoteId: string;
  readonly jobNumber: string;
  readonly sourceOfBusiness: string;
  readonly numberOfDrivers: number;
  readonly numberOfVehicles: number;
  readonly numberOfIncidents: number;
  readonly autoSegmentation: string;
  readonly dwellingSegmentation: string;
  readonly errorCodes: string[];
  readonly allowBind: string[];
  readonly quotingEmail: string;
  readonly quotingPhonenumber: string;
  readonly easOfferings?: EASEnsightenOfferings;
  age?: number;
  lifeEligibleforOnlineApplication?: boolean;
  lifeCoverageAmount?: number;
  lifePremiumAmount?: {
    [key in TLQQuoteTermLength]?: number;
  };
}
export interface Profile {
  username?: string;
  age?: number;
  email?: string;
  partyUID?: string;
  paperlessEBill?: boolean;
  paperlessEPolicy?: boolean;
  accounts?: EnsightenAccount[];
}
export interface Policy {
  policies?: EnsightenPolicy[];
  policy?: EnsightenPolicy;
  policyNumber?: string;
}
export interface Claims {
  claimFileNumber?: string;
  claimsLOB?: string;
  claimsType?: string;
  claimStatus?: string;
  claims?: EnsightenClaim[];
  emailStatus?: 'enrolled' | 'unenrolled' | '';
  phototypesUploaded?: string;
  photosUploaded?: string;
  photosSize?: string;
}

export interface Billing {
  billingLOB: string;
  billingCurrentPaymentPlan?: string;
  billingSelectedPaymentPlan?: string;
}

interface EnsightenPolicy {
  readonly policyNumber: string;
  readonly policyLOB: string;
  readonly policyState?: string | null;
  readonly policyStatus?: string;
  readonly policyType?: string | null;
  readonly autoPayStatus?: string;
  readonly autoPaymentMethod?: string;
}

interface EnsightenAccount {
  amicaNumber: string;
}

export interface EnsightenClaim {
  claimFileNumber: string;
  claimLOB: string;
  claimType: string;
  claimStatus: string;
}

export interface ABTests {
  activeTests: string[];
}

export interface EnsightenDataLayer {
  global: Global;
  message: Message;
  log: EnsightenLogItem;
  claims: Claims;
  policy: Policy;
  profile: Profile;
  quote: Quote;
  billing: Billing;
  abTests: ABTests;
}

export interface DataIDOptionalParams {
  name: string;
  value?: any;
  analyticsSection?: string;
  type?: string;
  indexNumber?: number;
  $hasDataIDOverRide?: boolean;
}
